import {yupResolver} from '@hookform/resolvers/yup'
import {FullscreenOverlayWithSpinner} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import type {PropsWithChildren, ReactElement} from 'react'
import {useEffect, useMemo} from 'react'
import {FormProvider, useForm} from 'react-hook-form'

import {LayoutSkeleton} from './components/LayoutSkeleton/index@desktop'
import {OrderMap} from './components/order-map/index@desktop'
import {ActionProvider, ConfigProvider, StatusProvider} from './contexts'
import * as classes from './OrderForm@desktop.classes'
import type {OrderFormProps as Props} from './TempForm@mobile'
import {getFormValidationSchema} from './TempForm@mobile'
import type {OrderFormState} from './types'
import {renderWidget} from './widgets/index@desktop'

export function OrderForm(props: PropsWithChildren<Props>): ReactElement {
  const {defaultValues, layout, isFetching, isOverlayVisible, onChange, onSubmit, config, outerDefaults, children} =
    props
  const i18n = useI18n()
  const resolver = useMemo(
    () =>
      yupResolver(
        getFormValidationSchema(outerDefaults?.taxi_class ?? '', i18n, config),
        {},
        {
          mode: 'async',
        },
      ),
    [outerDefaults?.taxi_class, config, i18n],
  )

  const form = useForm<OrderFormState>({
    defaultValues,
    resolver,
    mode: 'onBlur',
  })

  useEffect(() => {
    if (onChange) {
      return form.watch(onChange).unsubscribe
    }
  }, [form, onChange])

  useEffect(() => {
    if (outerDefaults) {
      form.reset(formValues => ({...formValues, ...outerDefaults}))
    }
  }, [form, onChange, outerDefaults])

  return (
    <>
      {isOverlayVisible && <FullscreenOverlayWithSpinner />}
      <div className={classes.root}>
        <div className={classes.formWrapper}>
          <div className={classes.form} data-e2e="full-form">
            <div>{children}</div>
            <StatusProvider isFetching={isFetching}>
              <ActionProvider onSubmit={onSubmit}>
                <ConfigProvider {...config}>
                  <FormProvider {...form}>
                    {layout ? renderWidget(layout, 'layout') : <LayoutSkeleton />}
                    <div className={classes.map}>
                      <OrderMap />
                    </div>
                  </FormProvider>
                </ConfigProvider>
              </ActionProvider>
            </StatusProvider>
          </div>
        </div>
      </div>
    </>
  )
}
